/**
 * Favorites state + star-rail events for the file list.
 *
 * Holds the Set of favorited file paths (GET /api/favorites/paths, loaded once per
 * page for authenticated users), exposes add/remove with optimistic star updates,
 * and renders VISIBLE feedback for every outcome (07e USER-ACTION FEEDBACK):
 * success toasts carry an action link ("choose collection" / "undo"), each error
 * class gets its own wording.
 *
 * Star semantics: filled = the document has >= 1 favorite entry (any collection).
 * Quick star click adds to the ROOT level; collections are assigned via the picker
 * (favorites-picker.js). Star-off removes the document from ALL collections.
 */

let _paths = null;      // Set<string> of favorited rel paths; null until loaded
let _folders = null;    // {path: collectionName} for docs in a named collection
let _loading = false;
let _folderReloadTimer = null;

function tr(key, fallback) {
	try {
		const lang = window.LANG || 'EN';
		const text = window.TranslationRegistry && window.TranslationRegistry.translate(key, lang);
		return (text && text !== key) ? text : fallback;
	} catch (e) { return fallback; }
}

export function favAddTooltip() { return tr('fav_add_tooltip', 'Add to favorites'); }
export function favRemoveTooltip() { return tr('fav_remove_tooltip', 'Remove from favorites'); }

function favErrorMessage(status, errorCode) {
	if (status === 401)
		return tr('fav_auth_required', 'Sign in to use favorites');
	if (errorCode === 'limit_exceeded')
		return tr('fav_limit', 'Favorites limit reached — remove something first');
	if (errorCode === 'forbidden')
		return tr('fav_forbidden', "You don't have access to this file");
	if (errorCode === 'not_found')
		return tr('fav_not_found', 'File not found');
	return tr('fav_error', 'Favorites action failed. Please try again.');
}

function isAuthenticated() {
	return !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
}

export function favHas(path) { return !!(_paths && _paths.has(path)); }

// Ambient folder badge: {id, name} of the (A-Z first) named collection holding
// this doc — name for the label, id for the deep-link to /favorites.
export function favFolder(path) { return (_folders && _folders[path]) || null; }

// Both dispatch channels: jQuery .trigger() does NOT reach addEventListener (22e).
function fireChanged() {
	try { $(document).trigger('favorites:changed'); } catch (e) {}
	try { document.dispatchEvent(new CustomEvent('favorites:changed', { bubbles: true })); } catch (e) {}
}

/**
 * Load the favorited-paths set once (no-op for guests); stars refresh on arrival.
 */
export function favEnsureLoaded() {
	if (_paths !== null || _loading || !isAuthenticated()) return;
	_loading = true;
	fetch('/api/favorites/paths', { credentials: 'same-origin' })
		.then(res => res.ok ? res.json() : null)
		.then(js => {
			const paths = js && js.data && js.data.paths;
			_paths = new Set(Array.isArray(paths) ? paths : []);
			_folders = (js && js.data && js.data.folders) || {};
			refreshStarsInDom();
			fireChanged();
		})
		.catch(() => { _loading = false; })
		.finally(() => { _loading = false; });
}

// Re-fetch the path->folder map after favorites change (a same-session move into
// a collection is otherwise invisible until reload). Debounced; folders only —
// _paths keeps its optimistic local state. Badge removal on star-off is already
// handled by the favHas() gate in refreshFolderBadgesInDom.
function scheduleFolderReload() {
	if (!isAuthenticated()) return;
	clearTimeout(_folderReloadTimer);
	_folderReloadTimer = setTimeout(() => {
		fetch('/api/favorites/paths', { credentials: 'same-origin' })
			.then(res => res.ok ? res.json() : null)
			.then(js => {
				const folders = js && js.data && js.data.folders;
				if (folders) { _folders = folders; refreshFolderBadgesInDom(); }
			})
			.catch(() => {});
	}, 800);
}

/**
 * Inject/refresh the ambient folder badge in each row. Shown only when the row's
 * document is BOTH favorited and in a named collection (root-only favorites carry
 * no folder). Rendered here (not in render.js) because the folder map loads async.
 */
export function refreshFolderBadgesInDom() {
	if (_folders === null) return;
	$('#file-list-container .file-item').each(function() {
		const $item = $(this);
		const path = $item.find('.fav-icon').attr('data-fav-path') || '';
		const folder = (path && favHas(path)) ? favFolder(path) : null;
		let $rail = $item.find('.fav-folder-rail');
		if (folder && folder.name) {
			if (!$rail.length) {
				// Anchor: click opens /favorites focused on this collection (deep-link).
				$rail = $('<a class="fav-folder-rail"><i class="bi bi-folder2 fav-folder-icon"></i><span class="fav-folder-name"></span></a>');
				$item.find('.file-item__right-rails').prepend($rail);
			}
			$rail.attr('href', folder.id ? ('/favorites#c=' + folder.id) : '/favorites');
			$rail.attr('title', tr('fav_folder_open', 'In collection: {n} — open in Favorites').replace('{n}', folder.name));
			$rail.find('.fav-folder-name').text(folder.name);
		} else if ($rail.length) {
			$rail.remove();
		}
	});
}

/**
 * Sync every rendered star with the state Set (used after load and after toggles).
 */
export function refreshStarsInDom() {
	if (_paths === null) return;
	$('#file-list-container .fav-icon').each(function() {
		const $icon = $(this);
		const on = _paths.has($icon.attr('data-fav-path') || '');
		$icon.attr('class', 'bi fav-icon ' + (on ? 'bi-star-fill fav-on' : 'bi-star'))
			.attr('title', on ? favRemoveTooltip() : favAddTooltip());
	});
	refreshFolderBadgesInDom();
}

// Visible transient with an optional action link (reuses .tg-toast visuals).
let _favToastTimer = null;
export function showFavToast(message, kind, actionLabel, onAction) {
	$('.fav-toast').remove();
	const $t = $('<div class="tg-toast fav-toast" role="status" aria-live="polite"></div>')
		.addClass('tg-toast--' + (kind || 'info'));
	$t.append($('<span class="fav-toast__text"></span>').text(message));
	if (actionLabel && onAction) {
		const $a = $('<button type="button" class="fav-toast__action"></button>').text(actionLabel);
		$a.on('click', function(e) {
			e.preventDefault();
			$t.removeClass('is-visible');
			onAction();
		});
		$t.append($a);
	}
	$('body').append($t);
	void $t[0].offsetWidth; // reflow so the enter transition restarts
	$t.addClass('is-visible');
	if (_favToastTimer) clearTimeout(_favToastTimer);
	_favToastTimer = setTimeout(() => { $t.removeClass('is-visible'); }, 5200);
}

function applyLocal(path, on) {
	if (_paths === null) _paths = new Set();
	if (on) _paths.add(path); else _paths.delete(path);
	refreshStarsInDom();
	fireChanged();
}

/**
 * Add to favorites (root by default). Optimistic star; reverts on failure.
 * Returns a promise resolving to the created item id (or null on failure).
 */
export function favAdd(href, path, collectionId, opts) {
	opts = opts || {};
	applyLocal(path, true);
	return fetch('/api/favorites/items', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'same-origin',
		body: JSON.stringify(collectionId ? { href: href, collection_id: collectionId } : { href: href })
	}).then(async (res) => {
		const js = await res.json().catch(() => ({}));
		if (res.ok && js.success) {
			const itemId = js.data && js.data.id;
			const collection = js.data && js.data.collection; // {id, name} when filed into a collection
			if (!opts.silent) {
				// If the doc landed in a (default) collection, name it; otherwise offer to file it.
				const actionLabel = collection && collection.name
					? tr('fav_in_collection', 'In {n}').replace('{n}', collection.name)
					: tr('fav_choose_collection', 'In a collection...');
				showFavToast(
					tr('fav_added', 'Added to Favorites'), 'ok',
					actionLabel,
					() => import('./favorites-picker.js').then(m => m.openFavPicker({ itemId: itemId, href: href, path: path })));
			}
			return itemId;
		}
		applyLocal(path, false);
		showFavToast(favErrorMessage(res.status, js.error), 'warn');
		return null;
	}).catch(() => {
		applyLocal(path, false);
		showFavToast(favErrorMessage(0, null), 'warn');
		return null;
	});
}

/**
 * Star-off: remove the document from ALL collections; toast offers undo (re-adds to root).
 */
export function favRemoveAll(href, path) {
	applyLocal(path, false);
	return fetch('/api/favorites/items', {
		method: 'DELETE',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'same-origin',
		body: JSON.stringify({ href: href, all_collections: true })
	}).then(async (res) => {
		const js = await res.json().catch(() => ({}));
		if (res.ok && js.success) {
			showFavToast(
				tr('fav_removed', 'Removed from Favorites'), 'info',
				tr('fav_undo', 'Undo'),
				() => { favAdd(href, path, null, { silent: true }); });
			return true;
		}
		applyLocal(path, true);
		showFavToast(favErrorMessage(res.status, js.error), 'warn');
		return false;
	}).catch(() => {
		applyLocal(path, true);
		showFavToast(favErrorMessage(0, null), 'warn');
		return false;
	});
}

/**
 * Toggle from any surface (star rail, long-tap sheet).
 */
export function favToggle(href, path) {
	return favHas(path) ? favRemoveAll(href, path) : favAdd(href, path, null);
}

// ---- Favorites-only filter (star toggle in the search filters bar) ----

let _filterOn = false;

export function favFilterOn() { return _filterOn; }

function syncFavFilterButton() {
	const $btn = $('[data-fav-filter]');
	if (!$btn.length) return;
	$btn.toggleClass('is-active', _filterOn)
		.attr('aria-pressed', _filterOn ? 'true' : 'false')
		.attr('title', _filterOn
			? tr('fav_filter_tooltip_on', 'Showing favorites only — click to show all files')
			: tr('fav_filter_tooltip_off', 'Show favorites only'));
	$btn.find('i').attr('class', 'bi ' + (_filterOn ? 'bi-star-fill' : 'bi-star'));
	// Count badge: number of favorited documents (hidden until the Set loads / when 0)
	const n = _paths ? _paths.size : 0;
	let $count = $btn.find('.fav-filter-toggle__count');
	if (n > 0) {
		if (!$count.length) {
			$count = $('<span class="fav-filter-toggle__count" aria-hidden="true"></span>');
			$btn.append($count);
		}
		$count.text(n > 99 ? '99+' : String(n));
	} else if ($count.length) {
		$count.remove();
	}
}

export function setFavFilter(on) {
	_filterOn = !!on;
	syncFavFilterButton();
	if (window.filterManager && typeof window.filterManager.applyFilters === 'function') {
		window.filterManager.applyFilters(false);
	}
}

let _filterInitDone = false;
function initFavFilterToggle() {
	if (_filterInitDone) return;
	const $btn = $('[data-fav-filter]');
	if (!$btn.length) return; // guest page or bar not rendered
	_filterInitDone = true;
	syncFavFilterButton();
	$btn.off('click.favFilter').on('click.favFilter', function(e) {
		e.preventDefault();
		favEnsureLoaded();
		setFavFilter(!_filterOn);
	});
	// Keep the filtered list honest: re-apply when favorites change while active
	// (star-off must drop the row live; async paths load must fill the list in).
	$(document).off('favorites:changed.favFilter').on('favorites:changed.favFilter', function() {
		syncFavFilterButton();  // keep the count badge live on any favorites change
		if (_filterOn && window.filterManager && typeof window.filterManager.applyFilters === 'function') {
			window.filterManager.applyFilters(false);
		}
	});
}

/**
 * Star-rail click events (desktop). The row itself opens the file — never bubble.
 */
export function initFavRailEvents(container) {
	favEnsureLoaded();
	initFavFilterToggle();
	// Keep folder badges fresh after same-session collection moves (debounced refetch)
	$(document).off('favorites:changed.favFolders').on('favorites:changed.favFolders', scheduleFolderReload);
	container.off('click.favRail', '.fav-rail')
		.on('click.favRail', '.fav-rail', function(e) {
			e.preventDefault();
			e.stopPropagation();
			const $icon = $(this).find('.fav-icon');
			if (!$icon.length) return;
			const href = $icon.attr('data-fav-href');
			const path = $icon.attr('data-fav-path');
			if (!href || !path) return;
			favToggle(href, path);
		});
}
